import React, { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { MdDownload, MdQrCode2 } from "react-icons/md";

const DEFAULT_THEME_COLOR = "#10B981";

const normalizeThemeColor = (color) =>
  /^#[0-9a-f]{6}$/i.test(String(color || "")) ? color : DEFAULT_THEME_COLOR;

const getQrColor = (color) => {
  const themeColor = normalizeThemeColor(color);
  const red = parseInt(themeColor.slice(1, 3), 16);
  const green = parseInt(themeColor.slice(3, 5), 16);
  const blue = parseInt(themeColor.slice(5, 7), 16);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance > 0.66 ? "#111827" : themeColor;
};

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const boundedRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + boundedRadius, y);
  context.arcTo(x + width, y, x + width, y + height, boundedRadius);
  context.arcTo(x + width, y + height, x, y + height, boundedRadius);
  context.arcTo(x, y + height, x, y, boundedRadius);
  context.arcTo(x, y, x + width, y, boundedRadius);
  context.closePath();
};

const loadCanvasImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

const drawContainedImage = (context, image, x, y, width, height) => {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const renderedWidth = image.naturalWidth * scale;
  const renderedHeight = image.naturalHeight * scale;
  context.drawImage(
    image,
    x + (width - renderedWidth) / 2,
    y + (height - renderedHeight) / 2,
    renderedWidth,
    renderedHeight
  );
};

const triggerBlobDownload = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${fileName || "plist-hotel-chat-qr"}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const BotQrCard = ({ url, color, image, botName, organizationName, fileName }) => {
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);
  const imageRequestRef = useRef(0);
  const [safeImage, setSafeImage] = useState();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    const requestId = imageRequestRef.current + 1;
    imageRequestRef.current = requestId;
    setSafeImage(undefined);

    if (!image) return undefined;

    const qrImage = new Image();
    qrImage.crossOrigin = "anonymous";
    qrImage.onload = () => {
      if (imageRequestRef.current === requestId) setSafeImage(image);
    };
    qrImage.onerror = () => {
      if (imageRequestRef.current === requestId) setSafeImage(undefined);
    };
    qrImage.src = image;

    return () => {
      qrImage.onload = null;
      qrImage.onerror = null;
    };
  }, [image]);

  useEffect(() => {
    if (!url || !containerRef.current) return;

    const options = {
      width: 220,
      height: 220,
      type: "canvas",
      data: url,
      image: safeImage,
      margin: 8,
      qrOptions: { errorCorrectionLevel: "H" },
      dotsOptions: {
        color: getQrColor(color),
        type: "rounded",
      },
      cornersSquareOptions: {
        color: getQrColor(color),
        type: "extra-rounded",
      },
      cornersDotOptions: {
        color: "#111827",
        type: "dot",
      },
      backgroundOptions: { color: "#ffffff" },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 5,
        imageSize: 0.3,
      },
    };

    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
      containerRef.current.innerHTML = "";
      qrCodeRef.current.append(containerRef.current);
    } else {
      qrCodeRef.current.update(options);
    }
  }, [color, safeImage, url]);

  const downloadQrCode = async () => {
    if (!qrCodeRef.current || !url) return;
    setDownloadError("");
    setIsDownloading(true);
    try {
      const qrBlob = await qrCodeRef.current.getRawData("png");
      if (!qrBlob) throw new Error("QR image is not ready");

      const canvas = document.createElement("canvas");
      canvas.width = 1600;
      canvas.height = 2000;
      const context = canvas.getContext("2d");
      const themeColor = normalizeThemeColor(color);
      const displayName = organizationName || botName || "Hotel guest assistant";

      context.fillStyle = themeColor;
      context.fillRect(0, 0, canvas.width, canvas.height);

      context.globalAlpha = 0.12;
      context.fillStyle = "#ffffff";
      context.beginPath();
      context.arc(80, 220, 310, 0, Math.PI * 2);
      context.fill();
      context.beginPath();
      context.arc(1530, 1720, 390, 0, Math.PI * 2);
      context.fill();
      context.globalAlpha = 1;

      context.fillStyle = "#ffffff";
      drawRoundedRect(context, 105, 100, 1390, 1800, 72);
      context.fill();

      if (safeImage) {
        const logo = await loadCanvasImage(safeImage);
        drawContainedImage(context, logo, 500, 175, 600, 180);
      } else {
        context.fillStyle = themeColor;
        context.textAlign = "center";
        context.font = "700 48px Arial, sans-serif";
        context.fillText(displayName, 800, 280, 1120);
      }

      context.fillStyle = "#111827";
      context.textAlign = "center";
      context.font = "700 68px Arial, sans-serif";
      context.fillText("Scan to chat with us", 800, 465, 1200);

      context.fillStyle = "#4B5563";
      context.font = "400 34px Arial, sans-serif";
      context.fillText("Book a stay, check your reservation, request room services", 800, 540);
      context.fillText("and get help directly from our digital concierge.", 800, 590);

      const qrObjectUrl = URL.createObjectURL(qrBlob);
      try {
        const qrImage = await loadCanvasImage(qrObjectUrl);
        context.shadowColor = "rgba(17, 24, 39, 0.15)";
        context.shadowBlur = 36;
        context.shadowOffsetY = 14;
        context.fillStyle = "#ffffff";
        drawRoundedRect(context, 310, 680, 980, 980, 55);
        context.fill();
        context.shadowColor = "transparent";
        context.drawImage(qrImage, 350, 720, 900, 900);
      } finally {
        URL.revokeObjectURL(qrObjectUrl);
      }

      context.fillStyle = themeColor;
      drawRoundedRect(context, 380, 1715, 840, 92, 46);
      context.fill();
      context.fillStyle = "#ffffff";
      context.font = "700 34px Arial, sans-serif";
      context.fillText("Point your camera at the QR code to begin", 800, 1773);

      const posterBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
      if (!posterBlob) throw new Error("Could not create poster image");
      triggerBlobDownload(posterBlob, fileName);
    } catch (_error) {
      setDownloadError("Could not download the branded QR poster. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!url) return null;

  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm">
      <div className="px-4 pt-4 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-500">
          <MdQrCode2 className="h-6 w-6" />
        </div>
        <h3 className="mt-2 font-bold text-navy-700">Scan to chat and book</h3>
        <p className="mt-1 text-xs text-gray-500">
          {organizationName || botName || "Hotel guest assistant"}
        </p>
      </div>

      <div className="flex justify-center p-3">
        <div ref={containerRef} className="overflow-hidden rounded-2xl bg-white p-1 shadow" />
      </div>

      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={downloadQrCode}
          disabled={isDownloading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600"
        >
          <MdDownload className="h-5 w-5" />
          {isDownloading ? "Preparing poster..." : "Download branded QR poster"}
        </button>
        {downloadError ? <p className="mt-2 text-xs text-red-600">{downloadError}</p> : null}
      </div>
    </div>
  );
};

export default BotQrCard;
